<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { ChevronDown } from 'lucide-vue-next'

/**
 * Menú desplegable de la barra de formato.
 *
 * Todo el menú corre con @mousedown.prevent: si el navegador moviera el foco al
 * botón, el contenteditable perdería la selección y el formato se aplicaría
 * sobre nada. Por eso también abre y cierra con click, no con mousedown.
 */
defineProps<{
  /** Texto del botón; si no viene, solo se muestra el icono del slot `button`. */
  label?: string
  title: string
  /** Ancho del panel, en clases de Tailwind. */
  menuWidth?: string
  /** Marca el botón como activo (por ejemplo, un color aplicado). */
  active?: boolean
  showChevron?: boolean
  /**
   * Deja que el panel tome el foco, para los que tienen campos de texto.
   * El editor pierde la selección al enfocar un input, así que quien use esto
   * debe guardarla y devolverla al aplicar.
   */
  allowFocus?: boolean
}>()

const emit = defineEmits<{ open: [] }>()

const isOpen = ref(false)
const anchorRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const menuStyle = ref<Record<string, string>>({})

function close() {
  isOpen.value = false
}

function toggleMenu() {
  isOpen.value = !isOpen.value
  if (isOpen.value) emit('open')
}

/** El panel se teletransporta al body para que no lo corte el scroll del compositor. */
watch(isOpen, async (open) => {
  if (!open) return
  const rect = anchorRef.value?.getBoundingClientRect()
  if (!rect) return
  menuStyle.value = { top: `${rect.bottom + 4}px`, left: `${rect.left}px` }
  await nextTick()
  const width = menuRef.value?.offsetWidth || 0
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
  menuStyle.value = { top: `${rect.bottom + 4}px`, left: `${left}px` }
})

function closeOnOutsideClick(event: MouseEvent) {
  const target = event.target as Node
  if (anchorRef.value?.contains(target) || menuRef.value?.contains(target)) return
  close()
}

watch(isOpen, (open) => {
  if (open) document.addEventListener('mousedown', closeOnOutsideClick, true)
  else document.removeEventListener('mousedown', closeOnOutsideClick, true)
})

onBeforeUnmount(() => document.removeEventListener('mousedown', closeOnOutsideClick, true))
</script>

<template>
  <div ref="anchorRef" class="relative flex-shrink-0">
    <button
      type="button"
      :title="title"
      class="h-[30px] px-1.5 rounded-md flex items-center gap-1 text-[12px] transition-colors hover:bg-secondary-100 dark:hover:bg-secondary-700"
      :class="isOpen || active
        ? 'bg-secondary-100 dark:bg-secondary-700 text-brand-navy dark:text-white'
        : 'text-secondary-600 dark:text-secondary-400 hover:text-brand-navy dark:hover:text-white'"
      @mousedown.prevent
      @click="toggleMenu"
    >
      <slot name="button" />
      <span v-if="label" class="truncate">{{ label }}</span>
      <ChevronDown v-if="showChevron !== false" class="w-3 h-3 opacity-70" />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="menuRef"
        :style="menuStyle"
        class="fixed z-[70] bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-xl overflow-hidden"
        :class="menuWidth || 'w-48'"
        @mousedown="allowFocus || $event.preventDefault()"
      >
        <slot :close="close" />
      </div>
    </Teleport>
  </div>
</template>
